import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { Clock, CheckCircle, XCircle, Award, ChevronRight, ChevronLeft } from 'lucide-react';

interface QuizQuestion {
  _id: string;
  questionText: string;
  questionType: 'qcm' | 'true_false' | 'text_input' | 'multiple_choice';
  options?: string[];
}

interface QuizSettings {
  timeLimit: number;
  maxAttempts: number;
  passingScore: number;
  showResults: boolean;
  showCorrectAnswers: boolean;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
}

interface QuizData {
  _id: string;
  title: string;
  description: string;
  questions: QuizQuestion[];
  settings: QuizSettings;
  difficulty: string;
}

interface Answer {
  questionId: string;
  answer: string | boolean | string[];
}

interface QuizAttemptResult {
  _id: string;
  score: number;
  isPassed: boolean;
  timeSpent: number;
  attemptNumber: number;
  results?: Array<{ question: string; answer: any; isCorrect: boolean; points: number }>;
}

const QuizAttempt: React.FC = () => {
  const { quizId, attemptId } = useParams<{ quizId: string; attemptId: string }>();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [quizAttemptResult, setQuizAttemptResult] = useState<QuizAttemptResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/student/quizzes/${quizId}`);
        setQuiz(response.data.quiz);

        // Initialize answers array
        setAnswers(response.data.quiz.questions.map((q: QuizQuestion) => ({
          questionId: q._id,
          answer: q.questionType === 'multiple_choice' ? [] : ''
        })));

        // Set time limit if applicable
        if (response.data.quiz.settings.timeLimit > 0) {
          setTimeLeft(response.data.quiz.settings.timeLimit * 60); // Convert minutes to seconds
        }
      } catch (error) {
        console.error('Error fetching quiz:', error);
        toast.error('Erreur lors du chargement du quiz.');
        navigate('/student/quizzes'); // Redirect if quiz not found or error
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [quizId, navigate]);

  useEffect(() => {
    if (timeLeft === null || quizAttemptResult) return; // Don't start timer if no time limit or quiz is submitted

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime === null) return null;
        if (prevTime <= 1) {
          clearInterval(timer);
          handleSubmitQuiz(); // Auto-submit when time runs out
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, quizAttemptResult]);

  const handleAnswerChange = (questionId: string, value: string | boolean) => {
    setAnswers(prevAnswers =>
      prevAnswers.map(ans =>
        ans.questionId === questionId ? { ...ans, answer: value } : ans
      )
    );
  };

  const handleMultipleChoiceChange = (questionId: string, option: string, isChecked: boolean) => {
    setAnswers(prevAnswers =>
      prevAnswers.map(ans => {
        if (ans.questionId === questionId) {
          const currentAnswers = Array.isArray(ans.answer) ? ans.answer : [];
          if (isChecked) {
            return { ...ans, answer: [...currentAnswers, option] };
          } else {
            return { ...ans, answer: currentAnswers.filter(a => a !== option) };
          }
        }
        return ans;
      })
    );
  };

  const handleSubmitQuiz = async () => {
    if (!quiz || submitting) return;
    setSubmitting(true);
    toast.loading('Soumission du quiz en cours...');

    try {
      const response = await api.post(`/student/quizzes/${quiz._id}/submit`, { answers });
      toast.dismiss();
      toast.success('Quiz soumis avec succès !');
      setQuizAttemptResult(response.data.attempt);
    } catch (error) {
      toast.dismiss();
      console.error('Error submitting quiz:', error);
      toast.error('Erreur lors de la soumission du quiz.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: QuizQuestion) => {
    const currentAnswer = answers.find(a => a.questionId === question._id)?.answer;

    switch (question.questionType) {
      case 'qcm':
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {question.options?.map((option, idx) => (
              <label key={idx} className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type={question.questionType === 'qcm' ? 'radio' : 'checkbox'}
                  name={question._id}
                  value={option}
                  checked={question.questionType === 'qcm' ? currentAnswer === option : (Array.isArray(currentAnswer) && currentAnswer.includes(option))}
                  onChange={(e) => question.questionType === 'qcm' ? handleAnswerChange(question._id, e.target.value) : handleMultipleChoiceChange(question._id, option, e.target.checked)}
                  className="form-radio h-4 w-4 text-brand-600 border-gray-300 focus:ring-brand-500"
                  disabled={!!quizAttemptResult}
                />
                <span className="ml-3 text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );
      case 'true_false':
        return (
          <div className="space-y-3">
            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name={question._id}
                value="true"
                checked={currentAnswer === true}
                onChange={() => handleAnswerChange(question._id, true)}
                className="form-radio h-4 w-4 text-brand-600 border-gray-300 focus:ring-brand-500"
                disabled={!!quizAttemptResult}
              />
              <span className="ml-3 text-gray-700">Vrai</span>
            </label>
            <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name={question._id}
                value="false"
                checked={currentAnswer === false}
                onChange={() => handleAnswerChange(question._id, false)}
                className="form-radio h-4 w-4 text-brand-600 border-gray-300 focus:ring-brand-500"
                disabled={!!quizAttemptResult}
              />
              <span className="ml-3 text-gray-700">Faux</span>
            </label>
          </div>
        );
      case 'text_input':
        return (
          <input
            type="text"
            value={currentAnswer as string}
            onChange={(e) => handleAnswerChange(question._id, e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
            placeholder="Votre réponse..."
            disabled={!!quizAttemptResult}
          />
        );
      default:
        return <p>Type de question non supporté.</p>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (!quiz) {
    return (
      <Layout>
        <div className="text-center py-10">
          <p className="text-lg text-gray-600">Quiz introuvable ou une erreur est survenue.</p>
          <button onClick={() => navigate('/student/quizzes')} className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-md">Retour aux quiz</button>
        </div>
      </Layout>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{quiz.title}</h1>
        <p className="text-gray-600 mb-6">{quiz.description}</p>

        {quizAttemptResult ? (
          // Quiz Results Display
          <div className="mt-8 text-center">
            <h2 className="text-2xl font-bold mb-4">
              {quizAttemptResult.isPassed ? (
                <span className="text-brand-600 flex items-center justify-center"><CheckCircle className="mr-2" /> Félicitations !</span>
              ) : (
                <span className="text-brand-600 flex items-center justify-center"><XCircle className="mr-2" /> Quiz non réussi</span>
              )}
            </h2>
            <p className="text-4xl font-extrabold text-gray-800 mb-4">
              Score: {quizAttemptResult.score}%
            </p>
            <div className="flex justify-center space-x-6 text-gray-700 mb-6">
              <p className="flex items-center"><Award className="mr-2" /> Tentative n°{quizAttemptResult.attemptNumber}</p>
              <p className="flex items-center"><Clock className="mr-2" /> Temps passé: {quizAttemptResult.timeSpent} min</p>
            </div>

            {quiz.settings.showResults && quizAttemptResult.results && (
              <div className="mt-8 text-left border-t pt-6">
                <h3 className="text-xl font-semibold mb-4">Détail des réponses :</h3>
                {quiz.questions.map((q, index) => {
                  const result = quizAttemptResult.results?.find(r => r.question === q._id);
                  const isCorrect = result?.isCorrect;
                  const userAnswer = result?.answer;

                  return (
                    <div key={q._id} className="mb-6 p-4 border rounded-lg bg-gray-50">
                      <p className="font-medium text-lg mb-2">{index + 1}. {q.questionText}</p>
                      <p className={`text-sm font-semibold ${isCorrect ? 'text-brand-600' : 'text-brand-600'}`}>
                        Votre réponse: {Array.isArray(userAnswer) ? userAnswer.join(', ') : (typeof userAnswer === 'boolean' ? (userAnswer ? 'Vrai' : 'Faux') : userAnswer)}
                        {isCorrect ? ' (Correct)' : ' (Incorrect)'}
                      </p>
                      {quiz.settings.showCorrectAnswers && !isCorrect && (
                        <p className="text-sm text-brand-600 mt-1">
                          Réponse correcte: {Array.isArray(q.correctAnswer) ? q.correctAnswer.join(', ') : (typeof q.correctAnswer === 'boolean' ? (q.correctAnswer ? 'Vrai' : 'Faux') : q.correctAnswer)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => navigate('/student/quizzes')}
              className="mt-8 px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              Retour aux quiz
            </button>
          </div>
        ) : (
          // Quiz Taking Interface
          <div className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                Question {currentQuestionIndex + 1} / {quiz.questions.length}
              </h2>
              {timeLeft !== null && (
                <div className="flex items-center text-lg font-medium text-gray-700">
                  <Clock className="mr-2" />
                  {Math.floor(timeLeft / 60).toString().padStart(2, '0')}:
                  {(timeLeft % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
              <p className="text-lg font-medium text-gray-900">{currentQuestion.questionText}</p>
            </div>

            {renderQuestion(currentQuestion)}

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                disabled={currentQuestionIndex === 0 || submitting}
                className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <ChevronLeft className="mr-2" /> Précédent
              </button>
              {currentQuestionIndex < quiz.questions.length - 1 ? (
                <button
                  onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                  disabled={submitting}
                  className="px-5 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Suivant <ChevronRight className="ml-2" />
                </button>
              ) : (
                <button
                  onClick={handleSubmitQuiz}
                  disabled={submitting}
                  className="px-5 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Soumission...' : 'Terminer le quiz'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QuizAttempt;
